import { useQuery } from "@tanstack/react-query";
import * as pdfjsLib from "pdfjs-dist";
import Worker from "pdfjs-dist/build/pdf.worker.min.mjs?worker";
import { useEffect, useState } from "react";
pdfjsLib.GlobalWorkerOptions.workerPort = new Worker();

import type { GetAttachmentConversionResponse } from "@nlc/shared-types";

import { attachmentsKeys } from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

type UsePdfToImagesResult = {
  error: Error | null;
  imageUrls: string[];
  isLoading: boolean;
};

interface PdfToImages {
  attachmentId: number;
  pdfUrl: string | undefined;
}

const usePdfToImages = ({
  attachmentId,
  pdfUrl,
}: PdfToImages): UsePdfToImagesResult => {
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const batchSize = 5;

  if (!pdfUrl) {
    throw new Error("Pdf URL is required");
  }

  const { data } = useQuery({
    queryFn: () =>
      apiClient
        .url(`/attachments/conversion/${attachmentId}`)
        .get()
        .json<GetAttachmentConversionResponse>(),
    queryKey: attachmentsKeys.getConversions(attachmentId),
  });

  useEffect(() => {
    if (data?.length) {
      setImageUrls(data?.map((img) => img.url));
    }
  }, [data]);

  useEffect(() => {
    const convertPdfToImages = async () => {
      if (!pdfUrl || data?.length) return;

      setIsLoading(true);
      setError(null);
      setImageUrls([]);

      try {
        const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
        const numPages = pdf.numPages;

        const renderPage = async (pageNum: number): Promise<string> => {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.5 });

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d", { willReadFrequently: true });

          if (!context) {
            throw new Error(`Could not get canvas context for page ${pageNum}`);
          }

          canvas.height = viewport.height;
          canvas.width = viewport.width;

          await page.render({
            canvasContext: context,
            viewport: viewport,
          }).promise;

          return canvas.toDataURL("image/jpeg", 0.8);
        };

        const newImageUrls: string[] = [];

        for (let i = 1; i <= numPages; i += batchSize) {
          const batchPages = Array.from(
            { length: Math.min(batchSize, numPages - i + 1) },
            (_, j) => renderPage(i + j),
          );
          const batchUrls = await Promise.all(batchPages);

          newImageUrls.push(...batchUrls);
        }

        setImageUrls(newImageUrls);
      } catch (error_) {
        console.error("Error converting PDF to images:", error_);
        setError(
          error_ instanceof Error ? error_ : new Error("Failed to convert PDF"),
        );
      } finally {
        setIsLoading(false);
      }
    };

    convertPdfToImages();
  }, [pdfUrl, batchSize, data]);

  return { error, imageUrls, isLoading };
};

export default usePdfToImages;
